import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {useSeo} from '../seo/SeoProvider';
const {profiles, priorityProducts, pathForPriority} = require('../seo/priority');

const Carousel = () => {
  const {products} = useSeo();
  const carouselItems = Object.entries(profiles).flatMap(([key, profile]) => {
    const product = priorityProducts(products, profile)[0];
    return product ? [{id:key, name:profile.label, image:product.image, path:pathForPriority(key)}] : [];
  });
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [carouselItems.length]);

  // Auto-scroll effect
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        
        // If at the end, go back to start
        if (scrollLeft >= scrollWidth - clientWidth - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scroll('right');
        }
      }
    }, 1500); // Scroll every 1.5 seconds

    return () => clearInterval(interval);
  }, [isPaused]);

  // Duplicate items for infinite scroll effect
  const duplicatedItems = [...carouselItems, ...carouselItems];
  if (!carouselItems.length) return null;

  return (
    <section data-testid="priority-carousel" className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between px-4 mb-8">
          <h3 data-testid="priority-carousel-heading" className="text-2xl font-bold text-slate-800">Notre sélection de matériel</h3>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              data-testid="priority-carousel-previous"
              aria-label="Matériels précédents"
              disabled={!canScrollLeft}
              className={`p-3 rounded-full transition-all ${
                canScrollLeft 
                  ? 'bg-white shadow-md hover:shadow-lg text-slate-700 hover:text-green-500' 
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              data-testid="priority-carousel-next"
              aria-label="Matériels suivants"
              disabled={!canScrollRight}
              className={`p-3 rounded-full transition-all ${
                canScrollRight 
                  ? 'bg-white shadow-md hover:shadow-lg text-slate-700 hover:text-green-500' 
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div 
          ref={scrollRef}
          onScroll={checkScroll}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          className="flex gap-6 overflow-x-auto scrollbar-hide px-4 pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {duplicatedItems.map((item, index) => (
            <Link
              key={`${item.id}-${index}`}
              to={item.path}
              data-testid={`priority-carousel-${item.id}-${index}`}
              className="flex-shrink-0 snap-start group"
            >
              <div className="relative w-72 h-48 rounded-2xl overflow-hidden bg-slate-50 flex items-center justify-center">
                <img
                  src={item.image}
                  alt={item.name}
                  data-testid={`priority-carousel-image-${index}`}
                  loading="lazy"
                  className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h4 className="text-xl font-bold text-white group-hover:text-green-300 transition-colors">
                    {item.name}
                  </h4>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-green-500/0 group-hover:bg-green-500/20 transition-colors duration-300 flex items-center justify-center">
                  <span className="text-white font-semibold opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                    Voir le catalogue
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Carousel;
